import {
    CropRegionContext,
    CroppedRegion,
    ImageSize,
    SelectedRegionCrop,
    SelectedRegionCropBasis
} from './domain';

export interface PrepareSelectedRegionCropInput {
    selectedRegion: CroppedRegion | null;
    originalImageSize: ImageSize | null;
    displayedImageSize: ImageSize | null;
    cropImageId?: string | null;
    cropRegionOriginal?: CroppedRegion | null;
    cropPreviewUrl?: string | null;
    localCropId?: string | null;
    cropBasis?: SelectedRegionCropBasis | null;
    mimeType?: string | null;
}

export interface PreparedSelectedRegionCrop {
    originalImageSize: ImageSize | null;
    displayedImageSize: ImageSize | null;
    selectedRegion: CroppedRegion | null;
    cropImageId: string | null;
    cropRegionOriginal: CroppedRegion | null;
    cropPreviewUrl: string | null;
    localCropId: string | null;
    selectedCrop: SelectedRegionCrop | null;
}

export interface ResolveSelectedRegionCropInput {
    selectedRegion: CroppedRegion | null;
    displayedImageSize: ImageSize | null;
    originalImageSize: ImageSize | null;
    minCropSidePixels?: number;
}

export interface ResolvedSelectedRegionCrop {
    selectedRegion: CroppedRegion;
    displayedRegion: CroppedRegion;
    cropRegionOriginal: CroppedRegion;
    cropWidth: number;
    cropHeight: number;
    clippedToImageBounds: boolean;
    isFullImage: boolean;
}

export const defaultMinCropSidePixels = 32;

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

function finiteNumber(value: number): number {
    return Number.isFinite(value) ? value : 0;
}

function rawDisplayPercentBounds(region: CroppedRegion): { x1: number; y1: number; x2: number; y2: number } {
    const values = [
        finiteNumber(region.x),
        finiteNumber(region.y),
        finiteNumber(region.width),
        finiteNumber(region.height)
    ];
    const isRatioRegion = values.every((value) => Math.abs(value) <= 1);
    const scale = isRatioRegion ? 100 : 1;
    const rawX1 = values[0] * scale;
    const rawY1 = values[1] * scale;
    const rawX2 = (values[0] + values[2]) * scale;
    const rawY2 = (values[1] + values[3]) * scale;

    return {
        x1: Math.min(rawX1, rawX2),
        y1: Math.min(rawY1, rawY2),
        x2: Math.max(rawX1, rawX2),
        y2: Math.max(rawY1, rawY2)
    };
}

function positiveSize(size: ImageSize | null | undefined): ImageSize | null {
    if (!size) return null;
    if (!Number.isFinite(size.width) || !Number.isFinite(size.height)) return null;
    if (size.width <= 0 || size.height <= 0) return null;

    return {
        width: Math.round(size.width),
        height: Math.round(size.height)
    };
}

export function normalizeDisplayRegion(region: CroppedRegion): CroppedRegion {
    const bounds = rawDisplayPercentBounds(region);
    const x1 = clamp(bounds.x1, 0, 100);
    const y1 = clamp(bounds.y1, 0, 100);
    const x2 = clamp(bounds.x2, 0, 100);
    const y2 = clamp(bounds.y2, 0, 100);

    return {
        x: x1,
        y: y1,
        width: Math.max(0, x2 - x1),
        height: Math.max(0, y2 - y1)
    };
}

export function displayPercentRegionToDisplayedPixels(region: CroppedRegion, displayedImageSize: ImageSize): CroppedRegion {
    const displaySize = positiveSize(displayedImageSize);
    const displayRegion = normalizeDisplayRegion(region);

    if (!displaySize) return { x: 0, y: 0, width: 0, height: 0 };

    return {
        x: (displayRegion.x / 100) * displaySize.width,
        y: (displayRegion.y / 100) * displaySize.height,
        width: (displayRegion.width / 100) * displaySize.width,
        height: (displayRegion.height / 100) * displaySize.height
    };
}

export function displayedPixelRegionToOriginalPixels(displayedRegion: CroppedRegion, displayedImageSize: ImageSize, originalImageSize: ImageSize): CroppedRegion {
    const displaySize = positiveSize(displayedImageSize);
    const originalSize = positiveSize(originalImageSize);

    if (!displaySize || !originalSize) return { x: 0, y: 0, width: 0, height: 0 };

    const scaleX = originalSize.width / displaySize.width;
    const scaleY = originalSize.height / displaySize.height;
    const x = clamp(Math.round(displayedRegion.x * scaleX), 0, originalSize.width - 1);
    const y = clamp(Math.round(displayedRegion.y * scaleY), 0, originalSize.height - 1);
    const maxWidth = originalSize.width - x;
    const maxHeight = originalSize.height - y;
    const width = clamp(Math.round(displayedRegion.width * scaleX), 1, maxWidth);
    const height = clamp(Math.round(displayedRegion.height * scaleY), 1, maxHeight);

    return { x, y, width, height };
}

export function displayRegionToOriginalPixels(region: CroppedRegion, displayedImageSize: ImageSize, originalImageSize: ImageSize): CroppedRegion {
    const displayedRegion = displayPercentRegionToDisplayedPixels(region, displayedImageSize);
    return displayedPixelRegionToOriginalPixels(displayedRegion, displayedImageSize, originalImageSize);
}

function regionsClose(a: CroppedRegion, b: CroppedRegion, tolerance = 0.01): boolean {
    return Math.abs(a.x - b.x) <= tolerance &&
        Math.abs(a.y - b.y) <= tolerance &&
        Math.abs(a.width - b.width) <= tolerance &&
        Math.abs(a.height - b.height) <= tolerance;
}

export function resolveSelectedRegionCrop(input: ResolveSelectedRegionCropInput): ResolvedSelectedRegionCrop {
    const displayedImageSize = positiveSize(input.displayedImageSize);
    const originalImageSize = positiveSize(input.originalImageSize);
    if (!input.selectedRegion) throw new Error('selectedRegion is required');
    if (!displayedImageSize) throw new Error('displayedImageSize must be greater than 0');
    if (!originalImageSize) throw new Error('originalImageSize must be greater than 0');

    const selectedRegion = normalizeDisplayRegion(input.selectedRegion);
    const displayedRegion = displayPercentRegionToDisplayedPixels(selectedRegion, displayedImageSize);
    const cropRegionOriginal = displayedPixelRegionToOriginalPixels(displayedRegion, displayedImageSize, originalImageSize);
    const minCropSidePixels = input.minCropSidePixels ?? defaultMinCropSidePixels;
    const isFullImage = regionsClose(selectedRegion, { x: 0, y: 0, width: 100, height: 100 });
    const rawBounds = rawDisplayPercentBounds(input.selectedRegion);
    const clippedToImageBounds = rawBounds.x1 < 0 || rawBounds.y1 < 0 || rawBounds.x2 > 100 || rawBounds.y2 > 100;

    if (selectedRegion.width <= 0 || selectedRegion.height <= 0 || cropRegionOriginal.width <= 0 || cropRegionOriginal.height <= 0) {
        throw new Error('selectedRegion is outside image bounds');
    }
    if (cropRegionOriginal.width < minCropSidePixels || cropRegionOriginal.height < minCropSidePixels) {
        throw new Error(`crop region must be at least ${minCropSidePixels}px wide and high`);
    }

    return {
        selectedRegion,
        displayedRegion,
        cropRegionOriginal,
        cropWidth: cropRegionOriginal.width,
        cropHeight: cropRegionOriginal.height,
        clippedToImageBounds,
        isFullImage
    };
}

export function cropContextToApiFields(input: PreparedSelectedRegionCrop | null): CropRegionContext {
    const cropImageId = input?.cropImageId || input?.selectedCrop?.crop_image_id || null;

    return {
        crop_image_id: cropImageId,
        original_image_size: input?.originalImageSize ? { ...input.originalImageSize } : null,
        displayed_image_size: input?.displayedImageSize ? { ...input.displayedImageSize } : null,
        crop_region_original: input?.cropRegionOriginal ? { ...input.cropRegionOriginal } : null,
        crop_preview_url: input?.cropPreviewUrl || null,
        local_crop_id: input?.localCropId || null
    };
}

export function prepareSelectedRegionCrop(input: PrepareSelectedRegionCropInput): PreparedSelectedRegionCrop {
    const originalImageSize = positiveSize(input.originalImageSize);
    const displayedImageSize = positiveSize(input.displayedImageSize);
    const selectedRegion = input.selectedRegion ? normalizeDisplayRegion(input.selectedRegion) : null;
    const cropImageId = input.cropImageId || input.localCropId || null;
    const cropRegionOriginal = input.cropRegionOriginal || (selectedRegion && displayedImageSize && originalImageSize
        ? displayRegionToOriginalPixels(selectedRegion, displayedImageSize, originalImageSize)
        : null);
    const cropPreviewUrl = input.cropPreviewUrl || null;
    const localCropId = input.localCropId || null;
    const selectedCrop: SelectedRegionCrop | null = selectedRegion && cropRegionOriginal
        ? {
            crop_image_id: cropImageId,
            crop_url: null,
            crop_preview_url: cropPreviewUrl,
            local_crop_id: localCropId,
            selected_region: { ...selectedRegion },
            original_image_size: originalImageSize ? { ...originalImageSize } : null,
            displayed_image_size: displayedImageSize ? { ...displayedImageSize } : null,
            crop_region_original: { ...cropRegionOriginal },
            mime_type: input.mimeType || 'image/webp',
            width: cropRegionOriginal.width,
            height: cropRegionOriginal.height,
            expires_at: null,
            crop_basis: input.cropBasis || 'user_adjusted_region',
            background_removed: false,
            source_mask_url: null
        }
        : null;

    return {
        originalImageSize,
        displayedImageSize,
        selectedRegion,
        cropImageId,
        cropRegionOriginal,
        cropPreviewUrl,
        localCropId,
        selectedCrop
    };
}
